import { createTheme } from '@mui/material/styles';

// Updated color palette based on screenshot
const colors = {
  primary: {
    main: '#004aad', // Deep blue from screenshot
    light: '#3373c4',
    dark: '#003580',
    contrastText: '#ffffff',
  },
  secondary: {
    main: '#A2C3A4', // Green from screenshot
    light: '#cbf199', // Light green from screenshot
    dark: '#869d96', // Muted green from screenshot
    contrastText: '#ffffff',
  },
  background: {
    default: '#ffffff', // White background from screenshot
    paper: '#ffffff',
    pattern: 'rgba(0, 74, 173, 0.05)', // Subtle blue for patterns
  },
  text: {
    primary: '#333333', // Dark text from screenshot
    secondary: '#666666', // Medium gray for secondary text
  },
  action: {
    active: '#004aad',
    hover: 'rgba(0, 74, 173, 0.08)',
  },
};

// Dark mode colors based on screenshot palette
const darkColors = {
  primary: {
    main: '#004aad', // Deep blue from screenshot
    light: '#3373c4',
    dark: '#003580',
    contrastText: '#ffffff',
  },
  secondary: {
    main: '#A2C3A4', // Green from screenshot
    light: '#cbf199', // Light green from screenshot
    dark: '#869d96', // Muted green from screenshot
    contrastText: '#ffffff',
  },
  background: {
    default: '#1A1A2A', // Darker background for dark mode
    paper: '#242436',
    pattern: 'rgba(0, 74, 173, 0.1)', // Subtle blue for patterns
  },
  text: {
    primary: '#ffffff',
    secondary: '#A2C3A4', // Using the green for secondary text in dark mode
  },
  action: {
    active: '#004aad',
    hover: 'rgba(0, 74, 173, 0.2)',
  },
};

// Create theme with Turkish typography settings
export const createTurkishTheme = (mode) => {
  const currentColors = mode === 'dark' ? darkColors : colors;

  return createTheme({
    palette: {
      mode,
      ...currentColors,
    },
    typography: {
      fontFamily: '"Segoe UI", "Open Sans", "Helvetica Neue", sans-serif',
      h1: {
        fontWeight: 600,
        letterSpacing: '-0.022em',
      },
      h2: {
        fontWeight: 600,
        letterSpacing: '-0.021em',
      },
      h3: {
        fontWeight: 600,
        letterSpacing: '-0.02em',
      },
      h4: {
        fontWeight: 600,
        letterSpacing: '-0.019em',
      },
      h5: {
        fontWeight: 600,
        letterSpacing: '-0.018em',
      },
      h6: {
        fontWeight: 600,
        letterSpacing: '-0.017em',
      },
      body1: {
        letterSpacing: '-0.011em',
        lineHeight: 1.6,
      },
      body2: {
        letterSpacing: '-0.01em',
        lineHeight: 1.5,
      },
      button: {
        fontWeight: 600,
        letterSpacing: '0.02em',
        textTransform: 'none',
      },
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            padding: '10px 24px',
            fontSize: '1rem',
            transition: 'all 0.3s ease-in-out',
          },
          contained: {
            backgroundImage: `linear-gradient(135deg, ${currentColors.primary.main} 0%, ${currentColors.primary.dark} 100%)`,
            '&:hover': {
              backgroundImage: `linear-gradient(135deg, ${currentColors.primary.dark} 0%, ${currentColors.primary.main} 100%)`,
              transform: 'translateY(-1px)',
              boxShadow: `0 4px 12px ${currentColors.primary.main}40`,
              backgroundColor: '#004aad', // Ensure the button maintains the primary color
            },
          },
          outlined: {
            borderWidth: 2,
            '&:hover': {
              borderWidth: 2,
              transform: 'translateY(-1px)',
            },
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 8,
              transition: 'all 0.3s ease-in-out',
              '&:hover': {
                transform: 'translateY(-1px)',
              },
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: mode === 'dark'
              ? `radial-gradient(circle at 100% 100%, ${currentColors.background.paper} 0%, ${currentColors.background.default} 100%)`
              : `radial-gradient(circle at 100% 100%, ${currentColors.background.paper} 0%, ${currentColors.background.default} 100%)`,
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: `
                repeating-linear-gradient(45deg, ${currentColors.background.pattern} 0px, ${currentColors.background.pattern} 2px, transparent 2px, transparent 10px),
                repeating-linear-gradient(-45deg, ${currentColors.background.pattern} 0px, ${currentColors.background.pattern} 2px, transparent 2px, transparent 10px)
              `,
              backgroundSize: '20px 20px',
              opacity: 0.5,
              zIndex: 0,
            },
          },
        },
      },
    },
  });
};
